/**
 * Converts the flat list HTML that Quill emits into nested lists.
 *
 * Quill 1 renders every list item as a sibling `<li>` and stores the indent
 * level on the item. `IndentStyle` writes that level as `margin-left`. Mail
 * clients, calendar descriptions, and other editors do not read `margin-left`
 * as nesting, so a sent email or a pasted copy loses the sub-levels.
 *
 * 1. Return the input unchanged when no item carries `margin-left`.
 * 2. Parse the HTML and visit each top-level `<ul>` or `<ol>`.
 * 3. Read each item's level from `margin-left`, then move the item into a list
 *    nested inside the previous shallower item. A level with no previous item
 *    gets a list nested directly inside the parent list.
 * 4. Remove `margin-left`. The nesting now carries the level.
 *
 * Both editors call this from `handleChange` (`index.tsx`). The reverse
 * direction, nested HTML back to Quill indent levels on reload or paste, is
 * `matchListIndent` in `nestedListClipboard.ts`.
 */
import { TAB_MULTIPLIER } from './IndentStyle';

const TOP_LEVEL_LIST_SELECTOR = 'ul, ol';

/** Reads the indent level `IndentStyle` stored on one list item. Missing or malformed is level 0. */
const readIndentLevel = (item: HTMLElement): number => {
  const level = Math.round(parseFloat(item.style.marginLeft) / TAB_MULTIPLIER);
  return Number.isNaN(level) ? 0 : Math.max(level, 0);
};

/** Removes the `margin-left` the editor wrote, and the empty `style` attribute it leaves behind. */
const clearIndentStyle = (item: HTMLElement): void => {
  item.style.marginLeft = '';
  if (!item.getAttribute('style')) {
    item.removeAttribute('style');
  }
};

/** True for an `<li>` element. A type predicate, so callers need no assertion. */
const isListItem = (node: Element): node is HTMLLIElement => node instanceof HTMLLIElement;

/**
 * Rebuilds one flat list in place. `stack[n]` is the list that receives items
 * at level `n`. The items leave the list first, so "last child" always means
 * the last item placed so far.
 */
const nestList = (list: HTMLElement, doc: Document): void => {
  const items = Array.from(list.children).filter(isListItem);
  for (const item of items) {
    item.remove();
  }
  const stack: Element[] = [list];
  for (const item of items) {
    const level = readIndentLevel(item);
    clearIndentStyle(item);
    while (stack.length > level + 1) {
      stack.pop();
    }
    while (stack.length < level + 1) {
      const parentList = stack[stack.length - 1];
      const previousItem = parentList.lastElementChild;
      const host = previousItem !== null && isListItem(previousItem) ? previousItem : parentList;
      const childList = doc.createElement(list.tagName);
      host.appendChild(childList);
      stack.push(childList);
    }
    stack[stack.length - 1].appendChild(item);
  }
};

/**
 * Returns the editor HTML with every indented list item nested inside a child
 * list. The result is idempotent: nested HTML with no `margin-left` passes
 * through unchanged.
 */
export const nestQuillListHtml = (html: string): string => {
  if (!html.includes('margin-left')) {
    return html;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const topLevelLists = Array.from(doc.body.querySelectorAll<HTMLElement>(TOP_LEVEL_LIST_SELECTOR)).filter(
    (list) => list.parentElement?.closest(TOP_LEVEL_LIST_SELECTOR) === null
  );
  for (const list of topLevelLists) {
    nestList(list, doc);
  }
  return doc.body.innerHTML;
};
